import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, auth, storage } from '../../firebase/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import WIcon from '../shared/WIcon';
import Modal from '../shared/Modal';
import CompanySelector from './CompanySelector';

const TIER_LABELS = ['', 'All Staff', 'Manager', 'Executive'];

// Categories must stay in sync with the Flutter app's category list
// (loysta_employee/lib/screens/offers_screen.dart and home_screen.dart).
const CATEGORIES = [
  { value: 'food',          label: 'Food' },
  { value: 'health',        label: 'Health' },
  { value: 'travel',        label: 'Travel' },
  { value: 'shopping',      label: 'Shopping' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'tech',          label: 'Tech' },
  { value: 'finance',       label: 'Finance' },
  { value: 'other',         label: 'Other' },
];

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB

const formatDate = (value) => {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : (value.toDate ? value.toDate() : new Date(value));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const tiersForMinLevel = (minLevel) => {
  const lvl = parseInt(minLevel, 10);
  return ['1', '2', '3'].filter((t) => parseInt(t, 10) >= lvl);
};

const tierVisibilityText = (minLevel) => {
  switch (String(minLevel)) {
    case '1': return 'All tiers';
    case '2': return 'Tier 2 & 3';
    case '3': return 'Tier 3 only';
    default: return '—';
  }
};

const OfferModal = ({ companies, onClose, onSaved }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    discountPercentage: '',
    targetCompanies: [],
    minTierLevel: '1',
    category: 'other',
    startDate: '',
    endDate: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);

  // Revoke any object URL we created so the preview doesn't leak memory.
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const setField = (k) => (e) => {
    let v = e.target.value;
    if (k === 'discountPercentage') {
      v = v.replace(/[^0-9]/g, '');
      v = String(Math.min(100, Math.max(0, parseInt(v || '0', 10))));
    }
    setForm((f) => ({ ...f, [k]: v }));
    if (error) setError('');
  };

  const onCompaniesChange = (selected) => {
    setForm((f) => ({ ...f, targetCompanies: selected.map((c) => c.id) }));
  };

  const onImagePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError('Image is too large — max 5 MB.');
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    if (error) setError('');
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!auth.currentUser) {
      setError('You are not signed in.');
      return;
    }
    if (!form.title.trim()) return setError('Offer title is required.');
    if (!form.discountPercentage) return setError('Discount percentage is required.');
    if (!form.startDate || !form.endDate) return setError('Both start and end dates are required.');
    if (new Date(form.startDate) > new Date(form.endDate)) return setError('End date must be after start date.');
    if (form.targetCompanies.length === 0) return setError('Select at least one company.');

    setSubmitting(true);
    try {
      let imageUrl = '';
      if (imageFile) {
        const ext = (imageFile.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `offer-images/${auth.currentUser.uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const objectRef = storageRef(storage, path);
        await uploadBytes(objectRef, imageFile, { contentType: imageFile.type });
        imageUrl = await getDownloadURL(objectRef);
      }

      await addDoc(collection(db, 'offers'), {
        title: form.title.trim(),
        description: form.description.trim(),
        discountPercentage: form.discountPercentage,
        companyIds: form.targetCompanies,
        tiers: tiersForMinLevel(form.minTierLevel),
        minTierLevel: form.minTierLevel,
        category: form.category,
        imageUrl,
        vendorId: auth.currentUser.uid,
        startDate: form.startDate,
        endDate: form.endDate,
        createdAt: new Date(),
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Error creating offer:', err);
      setError(err.message || 'Failed to create offer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="New offer" onClose={onClose} width={520}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div className="loysta-alert error">{error}</div>}

        <div className="form-group">
          <label>Offer title *</label>
          <input
            className="form-control"
            value={form.title}
            onChange={setField('title')}
            placeholder="e.g. Buy 1 Get 1 Free Coffee"
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            className="form-control"
            value={form.description}
            onChange={setField('description')}
            placeholder="What does the employee get?"
            rows={2}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="form-group">
            <label>Discount % *</label>
            <input
              className="form-control"
              type="text"
              inputMode="numeric"
              value={form.discountPercentage}
              onChange={setField('discountPercentage')}
              placeholder="50"
              required
            />
          </div>
          <div className="form-group">
            <label>Minimum tier *</label>
            <select className="form-control" value={form.minTierLevel} onChange={setField('minTierLevel')}>
              <option value="1">Tier 1 — All Staff</option>
              <option value="2">Tier 2 — Manager</option>
              <option value="3">Tier 3 — Executive</option>
            </select>
            <span className="form-hint">Visible to: {tierVisibilityText(form.minTierLevel)}</span>
          </div>
        </div>

        <div className="form-group">
          <label>Category *</label>
          <select className="form-control" value={form.category} onChange={setField('category')}>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <span className="form-hint">Used to group this offer in the employee app.</span>
        </div>

        <div className="form-group">
          <label>Offer image</label>
          {imagePreview ? (
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
              <img
                src={imagePreview}
                alt="Offer preview"
                style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }}
              />
              <button
                type="button"
                onClick={clearImage}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(15, 23, 42, 0.78)', color: 'white',
                  border: 'none', borderRadius: 6, padding: '4px 10px',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <label
              htmlFor="offer-image-input"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '18px 14px',
                background: '#f8fafc', border: '1.5px dashed #cbd5e1', borderRadius: 8,
                color: '#64748b', fontSize: 13, cursor: 'pointer',
              }}
            >
              <WIcon name="plus" size={16} color="#64748b" />
              Click to upload an image (optional, max 5 MB)
            </label>
          )}
          <input
            id="offer-image-input"
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onImagePicked}
            style={{ display: 'none' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="form-group">
            <label>Start date *</label>
            <input className="form-control" type="date" value={form.startDate} onChange={setField('startDate')} required />
          </div>
          <div className="form-group">
            <label>End date *</label>
            <input className="form-control" type="date" value={form.endDate} onChange={setField('endDate')} required />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Target companies *</label>
          <CompanySelector
            selectedCompanies={companies.filter((c) => form.targetCompanies.includes(c.id))}
            onCompaniesChange={onCompaniesChange}
            maxSelections={100}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button type="button" className="cancel-btn" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting} style={{ flex: 1, justifyContent: 'center' }}>
            {submitting ? 'Creating…' : 'Create offer'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const OfferForm = () => {
  const [offers, setOffers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchOffers = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const snap = await getDocs(query(collection(db, 'offers'), where('vendorId', '==', auth.currentUser.uid)));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dbb - da;
      });
      setOffers(list);
    } catch (err) {
      console.error('Error fetching offers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'companies'));
      setCompanies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  }, []);

  useEffect(() => { fetchOffers(); fetchCompanies(); }, [fetchOffers, fetchCompanies]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'offers', confirmDelete.id));
      setOffers((prev) => prev.filter((o) => o.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting offer:', err);
    }
  };

  const today = new Date();
  const filtered = offers.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.title || '').toLowerCase().includes(s) || (o.description || '').toLowerCase().includes(s);
  });

  return (
    <>
      {showModal && (
        <OfferModal
          companies={companies}
          onClose={() => setShowModal(false)}
          onSaved={fetchOffers}
        />
      )}

      {confirmDelete && (
        <Modal title="Delete offer" onClose={() => setConfirmDelete(null)} width={420}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: 0 }}>
              Delete <strong>{confirmDelete.title}</strong>? Employees will no longer see this offer in their app.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="cancel-btn" style={{ flex: 1 }} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button type="button" className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </Modal>
      )}

      <div className="loysta-card">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 12px' }}>
            <WIcon name="search" size={15} color="#94a3b8" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search offers…"
              style={{ border: 'none', background: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', width: '100%' }}
            />
          </div>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>{filtered.length} {filtered.length === 1 ? 'offer' : 'offers'}</span>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: '#00bf63', color: 'white',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#009950'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#00bf63'; }}
          >
            <WIcon name="plus" size={14} color="white" />
            New offer
          </button>
        </div>

        <table className="loysta-table">
          <thead>
            <tr>
              <th>Offer</th>
              <th>Discount</th>
              <th>Min tier</th>
              <th>Companies</th>
              <th>Active period</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading offers…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                {offers.length === 0
                  ? 'No offers yet. Click "New offer" to create your first one.'
                  : 'No offers match your search.'}
              </td></tr>
            ) : filtered.map((o) => {
              const tier = String(o.minTierLevel || '1');
              const start = o.startDate ? new Date(o.startDate) : null;
              const end = o.endDate ? new Date(o.endDate) : null;
              let status = 'active';
              if (end && end < today) status = 'inactive';
              else if (start && start > today) status = 'paused';
              const statusLabel = status === 'active' ? 'Active' : status === 'paused' ? 'Scheduled' : 'Ended';

              return (
                <tr key={o.id}>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{o.title}</div>
                    {o.description && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                      {o.discountPercentage || 0}%
                    </span>
                  </td>
                  <td>
                    <span className={`loysta-badge tier-${tier}`}>
                      <span className="dot" />
                      T{tier} · {TIER_LABELS[parseInt(tier, 10)] || ''}
                    </span>
                  </td>
                  <td style={{ color: '#64748b', fontSize: 13 }}>
                    {Array.isArray(o.companyIds) ? `${o.companyIds.length} ${o.companyIds.length === 1 ? 'company' : 'companies'}` : '—'}
                  </td>
                  <td style={{ color: '#94a3b8', fontSize: 12 }}>
                    {formatDate(o.startDate)} → {formatDate(o.endDate)}
                  </td>
                  <td><span className={`loysta-status ${status}`}>{statusLabel}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button className="loysta-icon-btn danger" onClick={() => setConfirmDelete(o)} title="Delete">
                        <WIcon name="trash" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default OfferForm;
